import React, { useEffect, useRef } from 'react';

// Barras de ecualizador de voz (sound waves) para una sesión pública de terminal.
// Extraído de PublicSessionView al partirlo (integración main→dev, epic B1).
const AudioEqualizerBars = ({ stream, isMuted, color = '#22c55e', numBars = 7 }) => {
    const canvasRef = useRef(null);
    const audioContextRef = useRef(null);
    const analyserRef = useRef(null);
    const animFrameRef = useRef(null);
    const sourceNodeRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');

        const drawFlat = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            ctx.fillStyle = '#475569';
            const barWidth = 3;
            const gap = 3;
            const totalW = numBars * barWidth + (numBars - 1) * gap;
            const startX = (canvas.width - totalW) / 2;
            for (let i = 0; i < numBars; i++) {
                ctx.beginPath();
                ctx.roundRect(startX + i * (barWidth + gap), (canvas.height / 2) - 1, barWidth, 2, 1);
                ctx.fill();
            }
        };

        if (!stream || isMuted) {
            if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
            drawFlat();
            return;
        }

        const audioTrack = stream.getAudioTracks ? stream.getAudioTracks()[0] : null;
        if (!audioTrack || !audioTrack.enabled) {
            if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
            drawFlat();
            return;
        }

        try {
            if (!audioContextRef.current) {
                audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
            }
            const actx = audioContextRef.current;
            if (actx.state === 'suspended') {
                actx.resume();
            }

            if (sourceNodeRef.current) {
                try { sourceNodeRef.current.disconnect(); } catch (e) {}
            }

            const analyser = actx.createAnalyser();
            analyser.fftSize = 64;
            analyserRef.current = analyser;

            let source;
            if (typeof actx.createMediaStreamTrackSource === 'function') {
                source = actx.createMediaStreamTrackSource(audioTrack);
            } else {
                source = actx.createMediaStreamSource(new MediaStream([audioTrack]));
            }
            source.connect(analyser);
            sourceNodeRef.current = source;

            const dataArray = new Uint8Array(analyser.frequencyBinCount);

            const render = () => {
                analyser.getByteFrequencyData(dataArray);
                ctx.clearRect(0, 0, canvas.width, canvas.height);

                const barWidth = 3;
                const gap = 3;
                const totalW = numBars * barWidth + (numBars - 1) * gap;
                const startX = (canvas.width - totalW) / 2;

                for (let i = 0; i < numBars; i++) {
                    const binIndex = 1 + i * 2;
                    const val = binIndex < dataArray.length ? dataArray[binIndex] : 0;
                    const barHeight = Math.max(2, (val / 255) * (canvas.height - 4));
                    const y = (canvas.height - barHeight) / 2;

                    ctx.fillStyle = val > 18 ? color : '#64748b';
                    ctx.beginPath();
                    ctx.roundRect(startX + i * (barWidth + gap), y, barWidth, barHeight, 1.5);
                    ctx.fill();
                }

                animFrameRef.current = requestAnimationFrame(render);
            };

            render();
        } catch (err) {
            console.warn('AudioEqualizer error:', err);
            drawFlat();
        }

        return () => {
            if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
            if (sourceNodeRef.current) {
                try { sourceNodeRef.current.disconnect(); } catch (e) {}
            }
        };
    }, [stream, isMuted, color, numBars]);

    return (
        <canvas
            ref={canvasRef}
            width={56}
            height={16}
            style={{ display: 'block', margin: '0 auto' }}
        />
    );
};

export default AudioEqualizerBars;
